import { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { listKnownAreas } from '../lib/areaColors';
import { applyOperations } from '../lib/api';
import EmptyState from './nav/EmptyState';
import DashboardSkeleton from './nav/DashboardSkeleton';

import { useDashboardData } from './dashboard/useDashboardData';
import DashboardTopBar from './dashboard/DashboardTopBar';
import ThemeHeader from './dashboard/ThemeHeader';
import NumeriImportanti from './dashboard/NumeriImportanti';
import CoperturaAree from './dashboard/CoperturaAree';
import ObiettiviGroup from './dashboard/ObiettiviGroup';
import MomentiCta from './dashboard/MomentiCta';
import StreakBanner from './dashboard/StreakBanner';
import OnboardingWizard from './nav/OnboardingWizard';

/**
 * Dashboard — pagina principale del planner.
 *
 * Composta da 6 sotto-componenti in `./dashboard/`. Questo file orchestra:
 *   - data fetching   → useDashboardData
 *   - filtri area     → state locale `areaFilter` (multi-select toggle)
 *   - collapse area   → state locale persistito su localStorage
 *   - empty state     → CTA al Setup
 *
 * Refactor Feb 2026 (P2): da ~540 righe → ~140 righe. Logica e UI invariate,
 * test e2e devono continuare a passare (data-testid preservati 1:1).
 */
export default function Dashboard() {
  const { data, loading, reload } = useDashboardData();

  // Onboarding: mostrato la prima volta che un utente apre la Dashboard
  // senza obiettivi. Persistente: una volta chiuso/completato non riappare.
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (loading || !data) return;
    if ((data.obiettivi || []).length > 0) return;
    try {
      if (localStorage.getItem('qp.onboarding.completed') === 'true') return;
    } catch { /* ignore */ }
    setShowOnboarding(true);
  }, [loading, data]);
  const dismissOnboarding = () => {
    try { localStorage.setItem('qp.onboarding.completed', 'true'); } catch { /* ignore */ }
    setShowOnboarding(false);
  };

  // Filtri attivi (multi-area). Vuoto = mostra tutte.
  const [areaFilter, setAreaFilter] = useState([]);
  const toggleAreaFilter = (area) =>
    setAreaFilter((prev) => (prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]));
  const clearAreaFilter = () => setAreaFilter([]);

  // Stato collapse aree, persistito su localStorage. Default: aperte.
  const [collapsedAreas, setCollapsedAreas] = useState(() => {
    try {
      const raw = localStorage.getItem('qp.dashboard.collapsedAreas');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleArea = (area) => {
    setCollapsedAreas((prev) => {
      const next = { ...prev, [area]: !prev[area] };
      try { localStorage.setItem('qp.dashboard.collapsedAreas', JSON.stringify(next)); } catch {/* ignore */}
      return next;
    });
  };

  // Derivati (sempre calcolati, anche durante loading — i hook devono restare stabili)
  const obiettivi = data?.obiettivi || [];
  // listKnownAreas() ritorna le chiavi statiche di AREE → no deps necessarie
  const knownAreas = useMemo(() => listKnownAreas(), []);
  const groupedAll = useMemo(
    () => knownAreas
      .map((a) => ({ area: a, items: obiettivi.filter((o) => (o.area || '').toLowerCase() === a) }))
      .filter((g) => g.items.length > 0),
    [knownAreas, obiettivi],
  );
  const grouped = useMemo(
    () => (areaFilter.length === 0 ? groupedAll : groupedAll.filter((g) => areaFilter.includes(g.area))),
    [groupedAll, areaFilter],
  );

  if (loading) {
    return <DashboardSkeleton />;
  }

  const annoNum = data?.anno?.anno;
  const numeri = data?.numeri_importanti || [];
  const copPct = data?.copertura?.percentuale || {};
  const copCount = data?.copertura?.conteggio || {};
  const isEmpty = obiettivi.length === 0 && numeri.length === 0 && !data?.anno?.tema;

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-canvas text-ink flex items-center justify-center p-6">
        <div className="max-w-xl w-full">
          <EmptyState
            icon={<Sparkles size={32} strokeWidth={1.5} />}
            title="Inizia da qui"
            desc="Non hai ancora obiettivi né un tema per l'anno. Il modo più veloce per partire è parlare con il coach: ti guida da tema → obiettivi → key results."
            actionLabel="Apri il Setup"
            actionTo="/setup"
            secondary={{ label: 'Manuale: Mese', to: '/mese' }}
            testid="dashboard-empty"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <DashboardTopBar annoNum={annoNum} />

      <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
        <StreakBanner />
        <ThemeHeader anno={data?.anno} onSaved={reload} />

        {/* GRID: KPI sinistra | Copertura destra */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-20" data-testid="kpi-coverage-grid">
          <NumeriImportanti items={numeri} />
          <CoperturaAree
            knownAreas={knownAreas}
            copPct={copPct}
            copCount={copCount}
            totale={data?.copertura?.totale_obiettivi || 0}
            areaFilter={areaFilter}
            onToggle={toggleAreaFilter}
            onClear={clearAreaFilter}
          />
        </section>

        {/* OBIETTIVI per area */}
        <section className="mb-20">
          <div className="flex items-baseline justify-between mb-10 border-b border-line pb-4">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">Obiettivi</h2>
            <span className="font-mono text-xs text-soft" data-testid="obiettivi-count">
              {areaFilter.length > 0
                ? `${grouped.reduce((s, g) => s + g.items.length, 0)} di ${obiettivi.length}`
                : `${obiettivi.length} totali`}
            </span>
          </div>

          {grouped.length === 0 ? (
            <p className="font-body text-sm text-mute italic">Nessun obiettivo definito. Vai al Setup per iniziare.</p>
          ) : (
            <div className="space-y-12">
              {grouped.map((g) => (
                <ObiettiviGroup
                  key={g.area}
                  area={g.area}
                  items={g.items}
                  isCollapsed={!!collapsedAreas[g.area]}
                  onToggle={() => toggleArea(g.area)}
                  onKrValueChange={async (krId, newValue) => {
                    try {
                      await applyOperations([{ entita: 'key_result', azione: 'aggiorna', id: krId, campi: { valore_attuale: newValue } }]);
                      toast.success('Valore aggiornato');
                      reload();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Aggiornamento fallito');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </section>

        <MomentiCta />

        <footer className="mt-20 pt-8 border-t border-line flex items-center justify-between">
          <span className="font-mono text-xs text-mute">Quantum Planner · single-user</span>
          {/* Gestione aree e azioni dati (seed) sono in /legenda → Impostazioni */}
        </footer>
      </div>

      <OnboardingWizard open={showOnboarding} onClose={dismissOnboarding} />
    </div>
  );
}
